/**
 * Phase 24.4b Step 4 — typed wrappers for `/api/recipes/*`.
 *
 * Types mirror backend Pydantic schemas in `app/schemas/recipe.py`
 * verbatim (Phase 22.3.7 + Phase 24.3 feedback). When backend schema
 * changes, update both sides in lock-step.
 */
import { api } from "./client";

// ---------------------------------------------------------------------------
// Types — mirror app/schemas/recipe.py
// ---------------------------------------------------------------------------

export interface IngredientInRecipe {
  name: string;
  amount: number;
  unit: string;
  is_optional: boolean;
  notes: string | null;
  category: string;
}

export interface RecipeStepRead {
  position: number;
  content: string;
  timer_seconds: number | null;
}

export interface RecipeRead {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  cuisine: string;
  meal_type: string;
  kcal_per_serving: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number;
  prep_time_min: number;
  serves: number;
  difficulty: number;
  tags: string[];
  dietary_tags: string[];
  allergens: string[];
  image_url: string | null;
}

export interface RecipeFullRead extends RecipeRead {
  ingredients: IngredientInRecipe[];
  steps: RecipeStepRead[];
}

export type RecipeFeedbackAction = "like" | "dislike";

export interface FeedbackRequest {
  action: RecipeFeedbackAction;
}

export interface FeedbackResponse {
  recipe_id: number;
  action_taken: RecipeFeedbackAction;
  is_favorite: boolean;
  is_disliked: boolean;
}

// ---------------------------------------------------------------------------
// Endpoints — `/api/recipes/*`
// ---------------------------------------------------------------------------

export interface ListRecipesParams {
  meal_type?: string;
  cuisine?: string;
  max_prep_time?: number;
  search?: string;
  limit?: number;
  offset?: number;
}

export async function listRecipes(
  params: ListRecipesParams = {},
): Promise<RecipeRead[]> {
  const { data } = await api.get<RecipeRead[]>("/recipes/", { params });
  return data;
}

export async function getRecipe(recipeId: number): Promise<RecipeFullRead> {
  const { data } = await api.get<RecipeFullRead>(`/recipes/${recipeId}`);
  return data;
}

export async function submitRecipeFeedback(
  recipeId: number,
  action: RecipeFeedbackAction,
): Promise<FeedbackResponse> {
  const { data } = await api.post<FeedbackResponse>(
    `/recipes/${recipeId}/feedback`,
    { action } satisfies FeedbackRequest,
  );
  return data;
}
